import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import './ToastContext.css';

const ToastContext = createContext(null);

const ICONS = {
  success: FaCheckCircle,
  error: FaExclamationCircle,
  warning: FaExclamationTriangle,
  info: FaInfoCircle
};

const DURATIONS = {
  success: 3500,
  info: 3500,
  warning: 4500,
  error: 4500
};

let toastSeq = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const showToast = useCallback((type, message) => {
    const text = String(message || '').trim();
    if (!text) return;
    const id = `toast-${Date.now()}-${toastSeq++}`;
    setToasts((current) => [{ id, type, message: text }, ...current]);
    window.setTimeout(() => dismiss(id), DURATIONS[type] || 4000);
  }, [dismiss]);

  const api = useMemo(() => ({
    showSuccess: (message) => showToast('success', message),
    showError: (message) => showToast('error', message),
    showWarning: (message) => showToast('warning', message),
    showInfo: (message) => showToast('info', message),
    dismiss
  }), [showToast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="app-toast-host" aria-live="polite" aria-relevant="additions text">
        {toasts.map((toast) => {
          const Icon = ICONS[toast.type] || FaInfoCircle;
          const role = toast.type === 'error' || toast.type === 'warning' ? 'alert' : 'status';
          return (
            <div key={toast.id} className={`app-toast app-toast-${toast.type}`} role={role}>
              <span className="app-toast-icon" aria-hidden="true"><Icon /></span>
              <span className="app-toast-label">{toast.type}</span>
              <p className="app-toast-message">{toast.message}</p>
              <button
                type="button"
                className="app-toast-close"
                aria-label="Close notification"
                onClick={() => dismiss(toast.id)}
              >
                <FaTimes />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
